import _ from 'underscore';
import defaultColumns from './defaults/default-columns';
import getDefaultName from './defaults/get-default-name';
import getDefaultFilter from './defaults/get-default-filter';
import getDefaultRoute, { getDefaultRouteForNewUI } from './defaults/get-default-route';
import { defaultColumnConversions } from './defaults/default-column-conversions';
import fillRoute from './util/fill-route';
import allGridsMetadata from './all-grids-metadata';

function getGridMetadata(entity) {
  return allGridsMetadata[entity] || {};
}

export function getGridName(entity, selection) {
  const {
    getName = () => getDefaultName(entity),
  } = getGridMetadata(entity);

  return getName(selection);
}

function getGridColumnsMetadata(entity, selection) {
  const {
    getColumnsMetadata = () => ({}),
  } = getGridMetadata(entity);

  return _.defaults({}, getColumnsMetadata(selection), defaultColumns);
}

export function getGridColumnConversions(
  entity,
  selection,
  { withPartialConversion = false } = {}
) {
  const {
    getColumnsConversionMetadata = _.constant({}),
  } = getGridMetadata(entity);

  const transform = withPartialConversion ? _.identity : _.invert;
  const transformedDefaultColumnConversions = transform(defaultColumnConversions);

  // columnConversionMetadata =
  // { [sourceColumnName]: '<targetColumnName>' }
  const columnConversionMetadata = _.defaults(
    {},
    getColumnsConversionMetadata(selection, withPartialConversion),
    transformedDefaultColumnConversions
  );

  // returns =>
  // { [targetColumnName]: { sourceColumnName } }
  return _.chain(columnConversionMetadata)
    .pairs()
    .map(([sourceColumnName, targetColumnName]) => [targetColumnName, { sourceColumnName }])
    .object()
    .value();
}

/**
 * A number, or a string containing a number.
 * @typedef {(string|string[])} PropertyPath
 */

/**
 * return associated properties to the given columns
 * @param {string} entity - the entity of the grid
 * @param {string[]} columnNames - the column names selected in the grid
 * @param {Selection} selection - the selection object used in ReportRule for inline download
 * @returns {PropertyPath[]} properties associated to the columns
 *  deep property are represented as array
 *  as an example:
 *  for Campaign Grid with columns ['CampaignId', 'Clicks']
 *  [
 *    'Id',
 *    ['PerformanceMetrics', 'Clicks'],
 *  ]
 */
export function getGridColumnsProperties(entity, columnNames, selection) {
  const allColumns = getGridColumnsMetadata(entity, selection);

  return _.map(_.flatten(_.map(columnNames, name => allColumns[name] || name)), (property) => {
    const frags = property.split('/');

    return frags.length === 1 ? property : frags;
  });
}

function stringifyPropertyPath(propertyPath) {
  return _.isArray(propertyPath) ? propertyPath.join('/') : propertyPath;
}

/**
 * return column names which correspond to the properties given
 * @param {string} entity - the entity of the grid
 * @param {PropertyPath[]} properties - the properties downloaded in inline download report
 * @param {Selection} selection - the selection object used in ReportRule for inline download
 * @returns {string[]} column names correspond to the properties given
 *  as an example:
 *  for Campaign Grid with properties ['Id', ['PerformanceMetrics', 'Clicks']]
 *  [
 *    'CampaignId',
 *    'Clicks',
 *  ]
 */
export function getGridColumnNames(entity, properties, selection) {
  const allColumns = getGridColumnsMetadata(entity, selection);

  return _.uniq(_.map(properties, (property) => {
    const strProperty = stringifyPropertyPath(property);

    const isInColumnProperties =
      columnProperties =>
        strProperty === columnProperties ||
        _.contains(columnProperties, strProperty);

    return _.findKey(allColumns, isInColumnProperties) || strProperty;
  }));
}

export function getGridFilter(entity, selection) {
  const {
    getFilter = () => getDefaultFilter(entity, selection),
  } = getGridMetadata(entity);

  return getFilter(selection);
}

export function getGridPage(entity, selection) {
  const {
    getPage = () => 'Campaigns',
  } = getGridMetadata(entity);

  return getPage(selection);
}

/**
 * return route for given entity & selection
 * @param {string} entity - the entity of the grid
 * @param {Selection} selection - the selection object used in ReportRule for inline download
 * @returns {string} the route for the grid, placeholders are filled
 */
export function getGridRoute(entity, selection) {
  const {
    getRoute = () => getDefaultRoute(entity, selection),
  } = getGridMetadata(entity);

  return fillRoute(getRoute(selection), selection);
}

export function getGridRouteForNewUI({
  getUrlSearch, entity, selection, serverContext,
}) {
  const {
    getRouteForNewUI = getDefaultRouteForNewUI,
  } = getGridMetadata(entity);
  const route = getRouteForNewUI({
    getUrlSearch, entity, selection, serverContext,
  });

  return `/campaign/vnext/${route}`;
}
